import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PolizaCreditoService } from '../../../../../services/finanzas/poliza-credito/poliza-credito.service';
import { EfectivoService } from '../../../../../services/finanzas/efectivo/efectivo.service';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { MasterService } from '../../../../master/services/master.service';
import { Option } from '../../../../../shared/models/option.model';
import { InputConfig } from '../../../../../shared/ui/form/input/input.component.config';
import { SelectConfig } from '../../../../../shared/ui/form/select/select.component.config';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import {
  CREDIT_POLICY_CASH_MOVEMENT,
  CreditPolicyOperationKind,
  CreditPolicyRow,
  creditPolicyOperationIcon,
  creditPolicyOperationTitle,
} from '../../models/credit-policy-operation.model';

export interface CreditPolicyOperationDialogData {
  policy: CreditPolicyRow;
  kind: CreditPolicyOperationKind;
  movementType?: number;
  editUid?: string;
}

function optionId(value: Option | string | null): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value.id || '');
}

@Component({
  selector: 'app-credit-policy-operation-dialog',
  templateUrl: './credit-policy-operation-dialog.component.html',
  styleUrls: ['./credit-policy-operation-dialog.component.scss'],
  standalone: false,
})
export class CreditPolicyOperationDialogComponent implements OnInit {
  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  submitted = false;
  saveError = '';
  changed = false;

  items: any[] = [];
  model: any = {};
  editing: any = null;

  readonly form = new FormGroup({
    fecha: new FormControl<Date | null>(null),
    comisionApertura: new FormControl<number | null>(0),
    descripcion: new FormControl(''),
    importe: new FormControl<number | null>(0),
    comisiones: new FormControl<number | null>(0),
    concepto: new FormControl(''),
    bankAccount: new FormControl<Option | string | null>(''),
    cash: new FormControl<Option | string | null>(''),
    activity: new FormControl<Option | string | null>(''),
    project: new FormControl<Option | string | null>(''),
    expenseProjectType: new FormControl<Option | string | null>(''),
  });

  readonly fechaConfig: InputConfig = { label: 'Fecha' };

  readonly comisionAperturaConfig: InputConfig = {
    label: 'Comisión de apertura',
    type: 'number',
    inputmode: 'decimal',
    step: '0.01',
    suffix: '€',
  };

  readonly descripcionConfig: InputConfig = { label: 'Descripción', maxLength: 100 };

  readonly importeConfig: InputConfig = {
    label: 'Importe',
    type: 'number',
    inputmode: 'decimal',
    step: '0.01',
    suffix: '€',
  };

  readonly comisionesConfig: InputConfig = {
    label: 'Comisiones',
    type: 'number',
    inputmode: 'decimal',
    step: '0.01',
    suffix: '€',
  };

  readonly conceptoConfig: InputConfig = { label: 'Concepto', maxLength: 100 };

  readonly bankAccountConfig: SelectConfig = {
    label: 'Cuenta bancaria',
    placeholder: 'Seleccionar cuenta…',
    options: [],
    autoSelectSingle: true,
  };

  readonly cashConfig: SelectConfig = {
    label: 'Caja / Efectivo',
    placeholder: 'Seleccionar caja…',
    options: [],
    autoSelectSingle: true,
  };

  readonly activityConfig: SelectConfig = {
    label: 'Actividad',
    placeholder: 'Seleccionar actividad…',
    options: [],
    autoSelectSingle: true,
  };

  readonly projectConfig: SelectConfig = {
    label: 'Proyecto',
    placeholder: 'Sin proyecto',
    options: [],
  };

  readonly expenseProjectTypeConfig: SelectConfig = {
    label: 'Tipo de proyecto de gasto',
    placeholder: 'Sin tipo',
    options: [],
  };

  projects: Option[] = [];
  expenseProjectTypes: Option[] = [];
  isProjectEnable = false;

  private requestedEditDone = false;

  static open(dialog: MatDialog, data: CreditPolicyOperationDialogData): Observable<boolean> {
    return dialog.open<CreditPolicyOperationDialogComponent, CreditPolicyOperationDialogData, boolean>(CreditPolicyOperationDialogComponent, {
      width: '760px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private dialogRef: MatDialogRef<CreditPolicyOperationDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: CreditPolicyOperationDialogData,
    private polizaCreditoService: PolizaCreditoService,
    private efectivoService: EfectivoService,
    private activityService: ActivityService,
    private masterService: MasterService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) { }

  get kind(): CreditPolicyOperationKind {
    return this.data.kind;
  }

  get title(): string {
    return creditPolicyOperationTitle(this.kind, this.data.movementType);
  }

  get icon(): string {
    return creditPolicyOperationIcon(this.kind, this.data.movementType);
  }

  get subtitle(): string {
    const policy = this.data.policy;
    return [policy.banco, policy.iban].filter(v => !!v).join(' · ');
  }

  get itemsLabel(): string {
    if (this.kind === 'constitution') return 'Constituciones registradas';
    if (this.kind === 'settlement') return 'Liquidaciones registradas';
    return 'Movimientos registrados';
  }

  get emptyText(): string {
    if (this.kind === 'constitution') return 'Todavía no hay ninguna constitución registrada.';
    if (this.kind === 'settlement') return 'Todavía no hay ninguna liquidación registrada.';
    return `Todavía no hay ningún movimiento de ${this.title.toLowerCase()} registrado.`;
  }

  get missingDate(): boolean {
    return !this.form.controls.fecha.value;
  }

  get missingDescription(): boolean {
    return this.kind === 'constitution' && !String(this.form.controls.descripcion.value || '').trim();
  }

  get missingCommission(): boolean {
    const value = this.form.controls.comisionApertura.value;
    return this.kind === 'constitution' && (value === null || value === undefined || String(value) === '');
  }

  get missingConcept(): boolean {
    return this.kind !== 'constitution' && !String(this.form.controls.concepto.value || '').trim();
  }

  get missingAmount(): boolean {
    return this.kind !== 'constitution' && !Number(this.form.controls.importe.value);
  }

  get missingActivity(): boolean {
    const uid = optionId(this.form.controls.activity.value);
    return !uid || uid === consts.EMPTY_GUID;
  }

  get missingBankAccount(): boolean {
    const uid = optionId(this.form.controls.bankAccount.value);
    return this.kind === 'bank' && (!uid || uid === consts.EMPTY_GUID);
  }

  get missingCash(): boolean {
    const uid = optionId(this.form.controls.cash.value);
    return this.kind === 'cash' && (!uid || uid === '0');
  }

  get hasErrors(): boolean {
    return this.missingDate || this.missingDescription || this.missingCommission || this.missingConcept
      || this.missingAmount || this.missingActivity || this.missingBankAccount || this.missingCash;
  }

  ngOnInit(): void {
    this.resetModel();
    this.load();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  startEdit(item: any): void {
    if (this.saving) return;
    this.editing = item;
    this.submitted = false;
    this.saveError = '';
    this.model = { ...item };
    this.patchFormFromModel();
  }

  cancelEdit(): void {
    this.editing = null;
    this.submitted = false;
    this.saveError = '';
    this.resetModel();
    this.patchFormFromModel();
    this.applySingleOptionDefaults();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    this.applyFormToModel();
    this.model.polizaCreditoId = this.data.policy.uid;

    const isEdit = !!this.editing;
    this.saving = true;
    this.savingMessage = isEdit ? 'Guardando cambios…' : 'Registrando…';
    this.dialogRef.disableClose = true;

    this.saveRequest(isEdit)
      .then(ok => {
        if (ok === false) throw new Error();
        this.changed = true;
        this.notify.ok(isEdit ? `${this.itemName} actualizada correctamente` : `${this.itemName} registrada correctamente`);
        this.dialogRef.disableClose = false;
        if (!isEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.saving = false;
        this.editing = null;
        this.submitted = false;
        this.resetModel();
        this.patchFormFromModel();
        this.applySingleOptionDefaults();
        this.reloadItems();
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = (error && error.error && error.error.message) || 'No se pudo guardar. Inténtalo de nuevo.';
      });
  }

  deleteItem(item: any): void {
    if (this.saving) return;
    this.confirm.open({
      title: `Eliminar ${this.itemName.toLowerCase()}`,
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará el registro y su asiento contable. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando…',
      errorText: 'No se pudo eliminar el registro',
      action: () => this.deleteRequest(item.uid).then(ok => { if (ok === false) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok(`${this.itemName} eliminada correctamente`);
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.reloadItems();
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  itemAmount(item: any): number {
    return this.kind === 'constitution' ? item.comisionApertura : item.importe;
  }

  itemText(item: any): string {
    return (this.kind === 'constitution' ? item.descripcion : item.concepto) || 'Sin descripción';
  }

  itemMeta(item: any): string {
    const parts: string[] = [];
    if (this.kind === 'bank' && item.bankAccountInfo && item.bankAccountInfo !== '0') parts.push(item.bankAccountInfo);
    if (this.kind === 'cash' && item.efectivoName) parts.push(item.efectivoName);
    if (item.activitiesName) parts.push(item.activitiesName);
    if (this.kind !== 'constitution' && Number(item.comisiones)) parts.push(`Comisiones ${Utils.FormatMoney(item.comisiones)} €`);
    return parts.join(' · ');
  }

  trackByUid(_: number, item: any): string {
    return item.uid;
  }

  private get itemName(): string {
    if (this.kind === 'constitution') return 'Constitución';
    if (this.kind === 'settlement') return 'Liquidación';
    return 'Operación';
  }

  private load(): void {
    this.ready = false;
    const uid = this.data.policy.uid;

    const lookups: Promise<unknown>[] = [
      this.activityService.getList()
        .then(list => { this.activityConfig.options = (list || []).map(a => ({ id: a.uid, display: a.name })); })
        .catch(() => { this.activityConfig.options = []; }),
      this.activityService.loadData(ActualSelected.activitySelected)
        .then(activity => { this.isProjectEnable = !!(activity && activity.isProject); })
        .catch(() => { this.isProjectEnable = false; })
        .then(() => {
          if (!this.isProjectEnable) return;
          return Promise.all([
            firstValueFrom(this.masterService.getProjects())
              .then(list => { this.projects = (list || []).map(p => ({ id: p.id, display: p.display })); })
              .catch(() => { this.projects = []; }),
            firstValueFrom(this.masterService.getExpenseProjectTypes())
              .then(list => { this.expenseProjectTypes = (list || []).map(e => ({ id: e.id, display: e.display })); })
              .catch(() => { this.expenseProjectTypes = []; }),
          ]).then(() => {
            this.projectConfig.options = this.projects;
            this.expenseProjectTypeConfig.options = this.expenseProjectTypes;
          });
        }),
    ];
    if (this.kind === 'bank') {
      lookups.push(firstValueFrom(this.masterService.getBankAccounts())
        .then(list => {
          this.bankAccountConfig.options = (list || []).map(account => ({
            id: account.id,
            display: [account.metadata && account.metadata.bank, account.display].filter(v => !!v).join(' · '),
          }));
        })
        .catch(() => { this.bankAccountConfig.options = []; }));
    }
    if (this.kind === 'cash') {
      lookups.push(this.efectivoService.list()
        .then(list => { this.cashConfig.options = (list || []).map(cash => ({ id: cash.uid, display: cash.nombre })); })
        .catch(() => { this.cashConfig.options = []; }));
    }

    Promise.all([this.listRequest(uid), Promise.all(lookups)])
      .then(([items]) => {
        this.items = Array.isArray(items) ? items : [];
        this.openRequestedEdit();
        this.applySingleOptionDefaults();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const target = this.items.find(i => i.uid === this.data.editUid);
    if (target) this.startEdit(target);
  }

  private reloadItems(): void {
    this.listRequest(this.data.policy.uid)
      .then(items => { this.items = Array.isArray(items) ? items : []; })
      .catch(() => this.notify.error('No se pudo recargar el listado'));
  }

  private findOption(options: any[], id: string): Option | string {
    if (!id || id === consts.EMPTY_GUID || id === '0') return '';
    const match = (options || []).find(o => String(o.id) === String(id));
    return match || '';
  }

  private patchFormFromModel(): void {
    this.form.reset({
      fecha: this.model.fecha || null,
      comisionApertura: this.model.comisionApertura != null ? this.model.comisionApertura : 0,
      descripcion: this.model.descripcion || '',
      importe: this.model.importe != null ? this.model.importe : 0,
      comisiones: this.model.comisiones != null ? this.model.comisiones : 0,
      concepto: this.model.concepto || '',
      bankAccount: this.findOption(this.bankAccountConfig.options, this.model.bankAccountsUid),
      cash: this.findOption(this.cashConfig.options, this.model.efectivoId),
      activity: this.findOption(this.activityConfig.options, this.model.activitiesUid),
      project: this.findOption(this.projectConfig.options, this.model.projectUid),
      expenseProjectType: this.findOption(this.expenseProjectTypeConfig.options, this.model.expenseProjectTypeUid),
    });
  }

  private applyFormToModel(): void {
    const value = this.form.getRawValue();
    this.model.fecha = value.fecha;

    if (this.kind === 'constitution') {
      this.model.comisionApertura = Number(value.comisionApertura) || 0;
      this.model.descripcion = String(value.descripcion || '').trim();
    } else {
      this.model.importe = Number(value.importe) || 0;
      this.model.comisiones = Number(value.comisiones) || 0;
      this.model.concepto = String(value.concepto || '').trim();
    }

    this.model.activitiesUid = optionId(value.activity);

    if (this.kind === 'bank') this.model.bankAccountsUid = optionId(value.bankAccount);
    if (this.kind === 'cash') this.model.efectivoId = optionId(value.cash);

    if (this.kind === 'bank' || this.kind === 'cash') {
      this.model.projectUid = optionId(value.project) || consts.EMPTY_GUID;
      this.model.expenseProjectTypeUid = optionId(value.expenseProjectType) || consts.EMPTY_GUID;
    }
  }

  private resetModel(): void {
    if (this.kind === 'constitution') {
      this.model = {
        uid: '0',
        polizaCreditoId: '',
        fecha: new Date(),
        comisionApertura: 0,
        descripcion: '',
        activitiesUid: consts.EMPTY_GUID,
        activitiesName: '',
      };
      return;
    }
    if (this.kind === 'settlement') {
      this.model = {
        uid: '0',
        polizaCreditoId: '',
        fecha: new Date(),
        concepto: '',
        importe: 0,
        comisiones: 0,
        activitiesUid: consts.EMPTY_GUID,
        activitiesName: '',
      };
      return;
    }
    if (this.kind === 'bank') {
      this.model = {
        uid: '',
        polizaCreditoId: '0',
        bankAccountsUid: '',
        tiposMovimientoBancoId: this.data.movementType,
        fecha: new Date(),
        comisiones: 0,
        concepto: '',
        importe: 0,
        bankAccountInfo: '0',
        activitiesUid: consts.EMPTY_GUID,
        activitiesName: '',
        projectUid: consts.EMPTY_GUID,
        expenseProjectTypeUid: consts.EMPTY_GUID,
      };
      return;
    }
    this.model = {
      uid: '',
      polizaCreditoId: '',
      tiposMovimientoEfectivoId: this.data.movementType,
      efectivoId: '0',
      fecha: new Date(),
      comisiones: 0,
      concepto: this.data.movementType === CREDIT_POLICY_CASH_MOVEMENT.ingreso ? 'Ingreso de efectivo' : 'Reintegro de efectivo',
      importe: 0,
      efectivoName: '',
      activitiesUid: consts.EMPTY_GUID,
      activitiesName: '',
    };
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;
    this.applyCurrentActivity();
    this.selectSingle(this.form.controls.activity, this.activityConfig.options);
    if (this.kind === 'bank') this.selectSingle(this.form.controls.bankAccount, this.bankAccountConfig.options);
    if (this.kind === 'cash') this.selectSingle(this.form.controls.cash, this.cashConfig.options);
  }

  private applyCurrentActivity(): void {
    const control = this.form.controls.activity;
    if (optionId(control.value)) return;
    const current = String(ActualSelected.activitySelected || '').toLowerCase();
    if (!current) return;
    const match = (this.activityConfig.options || []).find(option => String(option.id).toLowerCase() === current);
    if (match) control.setValue(match);
  }

  private selectSingle(control: FormControl<Option | string | null>, options: any[]): void {
    if (!options || options.length !== 1) return;
    if (optionId(control.value)) return;
    control.setValue(options[0]);
  }

  private listRequest(uid: string): Promise<any[]> {
    if (this.kind === 'constitution') return this.polizaCreditoService.constitucionList(uid);
    if (this.kind === 'settlement') return this.polizaCreditoService.liquidacionesList(uid);
    if (this.kind === 'bank') return this.polizaCreditoService.movimientoBancoPolizaCreditoList(uid, this.data.movementType);
    return this.polizaCreditoService.movimientoEfectivoPolizaCreditoList(uid, this.data.movementType);
  }

  private saveRequest(isEdit: boolean): Promise<boolean> {
    if (this.kind === 'constitution') {
      return isEdit ? this.polizaCreditoService.constitucionUpdate(this.model) : this.polizaCreditoService.constitucionCreate(this.model);
    }
    if (this.kind === 'settlement') {
      return isEdit ? this.polizaCreditoService.liquidacionesUpdate(this.model) : this.polizaCreditoService.liquidacionesCreate(this.model);
    }
    if (this.kind === 'bank') {
      return isEdit
        ? this.polizaCreditoService.movimientoBancoPolizaCreditoUpdate(this.model)
        : this.polizaCreditoService.movimientoBancoPolizaCreditoCreate(this.model);
    }
    return isEdit
      ? this.polizaCreditoService.movimientoEfectivoPolizaCreditoUpdate(this.model)
      : this.polizaCreditoService.movimientoEfectivoPolizaCreditoCreate(this.model);
  }

  private deleteRequest(uid: string): Promise<boolean> {
    if (this.kind === 'constitution') return this.polizaCreditoService.constitucionDelete(uid);
    if (this.kind === 'settlement') return this.polizaCreditoService.liquidacionesDelete(uid);
    if (this.kind === 'bank') return this.polizaCreditoService.movimientoBancoPolizaCreditoDelete(uid);
    return this.polizaCreditoService.movimientoEfectivoPolizaCreditoDelete(uid);
  }

  private itemDetails(item: any): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: formatDate(item.fecha, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: this.kind === 'constitution' ? 'Descripción' : 'Concepto', value: this.itemText(item) });
    details.push({ label: this.kind === 'constitution' ? 'Comisión de apertura' : 'Importe', value: Utils.FormatMoney(this.itemAmount(item) || 0) + ' €' });
    return details;
  }
}
